import React, { useEffect, useRef } from "react";
import { Box, Card, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import * as echarts from "echarts";

const ChartItem = ({ title, totalDevices, dailyData = [] }) => {
  const theme = useTheme();
  const chartRef = useRef(null);
  const chartInstance = useRef(null);

  useEffect(() => {
    const chartDom = chartRef.current;
    if (!chartDom || dailyData.length === 0) return;

    const chart = echarts.init(chartDom);
    chartInstance.current = chart;

    const uniqueDates = [];
    const firstIndices = {};
    dailyData.forEach((item, idx) => {
      const dateKey = item?.day ? String(item.day).substring(0, 10) : `Day${idx}`;
      if (!uniqueDates.includes(dateKey)) {
        uniqueDates.push(dateKey);
        firstIndices[dateKey] = idx;
      }
    });

    const onlineSeries = dailyData.map((item) => item?.online ?? 0);
    const offlineSeries = dailyData.map((item) => item?.offline ?? 0);



    const option = {
      grid: {
        top: 20,
        bottom: 25,
        left: 30,
        right: 15,
        containLabel: true,
      },
      tooltip: {
        trigger: "axis",
        axisPointer: {
          type: "shadow",
        },
        backgroundColor: "#fff",
        borderColor: "#e0e4f0",
        borderWidth: 1,
        textStyle: {
          color: "#333",
          fontSize: 12,
        },
        padding: [8, 12],
        extraCssText: "box-shadow: 0px 2px 8px rgba(0,0,0,0.08); border-radius: 6px;",
        formatter: (params) => {
          const idx = params[0].dataIndex;
          const item = dailyData[idx];
          if (!item) return "";
          
          const dateKey = item.day ? String(item.day).substring(0, 10) : `Day${idx}`;
          const dateIdx = uniqueDates.indexOf(dateKey);
          const dayName = `D${dateIdx + 1}`;
          const timeSuffix = item.day && String(item.day).length > 10 ? ` ${String(item.day).substring(11, 16)}` : "";

          let onlineVal = 0;
          let offlineVal = 0;
          params.forEach((p) => {
            if (p.seriesName === "Online") onlineVal = p.value;
            if (p.seriesName === "Offline") offlineVal = p.value;
          });
          return `
            <div style="font-weight: bold; margin-bottom: 4px;">${dayName}${timeSuffix}</div>
            <div style="display: flex; align-items: center; gap: 8px; justify-content: space-between;">
              <span style="display: flex; align-items: center; gap: 6px;">
                <span style="display: inline-block; width: 8px; height: 8px; border-radius: 50%; background-color: ${theme.palette.primary.main};"></span>
                Online
              </span>
              <span style="font-weight: bold;">${onlineVal}</span>
            </div>
            <div style="display: flex; align-items: center; gap: 8px; justify-content: space-between; margin-top: 2px;">
              <span style="display: flex; align-items: center; gap: 6px;">
                <span style="display: inline-block; width: 8px; height: 8px; border-radius: 50%; background-color: ${theme.palette.primary.greyThread || "#D5D5D5"};"></span>
                Offline
              </span>
              <span style="font-weight: bold;">${offlineVal}</span>
            </div>
          `;
        },
      },
      xAxis: {
        type: "category",
        data: dailyData.map((_, idx) => idx),
        axisLine: {
          lineStyle: {
            color: "#e0e4f0",
          },
        },
        axisLabel: {
          color: "#707ca8",
          fontSize: 11,
          interval: 0,
          formatter: (value, index) => {
            const item = dailyData[index];
            if (!item) return "";
            const dateKey = item.day ? String(item.day).substring(0, 10) : `Day${index}`;
            if (firstIndices[dateKey] === index) {
              const dateIdx = uniqueDates.indexOf(dateKey);
              return `D${dateIdx + 1}`;
            }
            return "";
          },
        },
        splitLine: {
          show: true,
          interval: (index) => {
            const item = dailyData[index];
            if (!item) return false;
            const dateKey = item.day ? String(item.day).substring(0, 10) : `Day${index}`;
            return firstIndices[dateKey] === index && index > 0;
          },
          lineStyle: {
            color: "#e0e4f0",
          },
        },
      },
      yAxis: {
        type: "value",
        minInterval: 1,
        axisLine: {
          show: false,
        },
        axisLabel: {
          color: "#707ca8",
          fontSize: 11,
        },
        splitLine: {
          lineStyle: {
            color: "#f0f2f8",
          },
        },
      },
      series: [
        {
          name: "Online",
          type: "bar",
          stack: "status",
          emphasis: {
            focus: "series",
          },
          itemStyle: {
            color: theme.palette.primary.main,
          },
          data: onlineSeries,
        },
        {
          name: "Offline",
          type: "bar",
          stack: "status",
          emphasis: {
            focus: "series",
          },
          itemStyle: {
            color: theme.palette.primary.greyThread || "#D5D5D5",
            borderRadius: [2, 2, 0, 0],
          },
          data: offlineSeries,
        },
      ],
    };

    chart.setOption(option);

    const handleResize = () => chart.resize();
    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
      chart.dispose();
    };
  }, [dailyData]);

  if (dailyData.length === 0) {
    return (
      <Card
        sx={{
          flex: 1,
          minWidth: "300px",
          p: 2.5,
          borderRadius: "8px",
          border: "1px solid #e0e4f0",
          boxShadow: "0px 2px 8px rgba(0,0,0,0.04)",
          display: "flex",
          flexDirection: "column",
          minHeight: "260px",
        }}
      >
        <Typography
          sx={{
            fontSize: "14px",
            color: "#172264",
            fontWeight: 600,
            mb: 2,
          }}
        >
          {title}
        </Typography>
        <Box sx={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <Typography sx={{ color: "#707ca8", fontSize: "14px" }}>No Data</Typography>
        </Box>
      </Card>
    );
  }

  return (
    <Card
      sx={{
        flex: 1,
        minWidth: "300px",
        p: 2.5,
        borderRadius: "8px",
        border: "1px solid #e0e4f0",
        boxShadow: "0px 2px 8px rgba(0,0,0,0.04)",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Typography
        sx={{
          fontSize: "14px",
          color: "#172264",
          fontWeight: 600,
          mb: 2,
        }}
      >
        {title}
      </Typography>
      <Box sx={{ width: "100%", height: "180px" }} ref={chartRef} />
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 3,
          mt: 2,
          justifyContent: "flex-start",
          pl: 1,
        }}
      >
        <Typography sx={{ fontSize: "12px", color: "#172264", fontWeight: 600 }}>
          Total: {totalDevices}
        </Typography>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Box sx={{ width: "10px", height: "10px", bgcolor: theme.palette.primary.main, borderRadius: "2px" }} />
          <Typography sx={{ fontSize: "12px", color: "#707ca8" }}>Online</Typography>
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Box sx={{ width: "10px", height: "10px", bgcolor: theme.palette.primary.greyThread || "#D5D5D5", borderRadius: "2px" }} />
          <Typography sx={{ fontSize: "12px", color: "#707ca8" }}>Offline</Typography>
        </Box>
      </Box>
    </Card>
  );
};

const DeviceStatusChart = ({ chartsData = {} }) => {
  const switchStatus = chartsData.switchStatus || {};
  const apStatus = chartsData.apStatus || {};

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "row",
        flexWrap: "wrap",
        gap: 3,
        width: "100%",
      }}
    >
      <ChartItem
        title="Site Network Status - Switch (Occurred Offline)"
        totalDevices={switchStatus.totalUniqueDevices ?? 0}
        dailyData={switchStatus.daily || []}
      />
      <ChartItem
        title="Site Network Status - Access Point (Occurred Offline)"
        totalDevices={apStatus.totalUniqueDevices ?? 0}
        dailyData={apStatus.daily || []}
      />
    </Box>
  );
};

export default DeviceStatusChart;
